import {Component, Inject, Input, LOCALE_ID, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {endWith} from 'rxjs/operators';
import {forkJoin, Observable} from 'rxjs';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {Country, Customer} from '~core/models';
import {AttributeService, NavigationService, StorageService} from '~core/services';

/**
 * Handles contact management.
 */
@UntilDestroy()
@Component({
  selector: 'app-attributes',
  templateUrl: 'attributes.component.html',
  styleUrls: ['../../../customer-overview.component.scss']
})
export class AttributesComponent implements OnInit, OnDestroy {
  public selectedClub: any;
  public loadingAttributes: boolean = true;
  public attributeLoadingId: any;
  public countries: Array<Country> = [];
  public attributes: Array<any> = [];
  public clubAttributes: Array<any> = [];
  public disabledAttributes: number;
  public updateAttributeForm: UntypedFormGroup;
  public user: Customer;
  public updateDataLoading: boolean = false;

  public spinnerLoading: boolean;
  public spinnerName: string = 'clubAttributesLoading';

  public spinnerSubmitName: string = 'clubAttributesSubmitted';

  @Input() public customer: Customer;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private storage: StorageService,
    private attributeService: AttributeService,
    public customerService: CustomerOverViewService,
    public navigationService: NavigationService,
    public buttonsTranslations: ButtonsTranslations,
    public spinnerService: SpinnerService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe(club => this.selectedClub = club);
  }

  /**
   * Create the form and fetch the countries and the current user
   */
  public ngOnInit(): any {
    this.spinnerLoading = true;

    this.attributeService.castCustomerAttributes.pipe(untilDestroyed(this))
      .subscribe((data: any) => {
        this.attributes = data;
      });

    this.attributeService.castClubAttributes.pipe(untilDestroyed(this))
      .subscribe((data: any) => {
        this.clubAttributes = data;
      });

    this.getAttributes();
    this.fillAttributeFormEmpty();
  }

  public ngOnDestroy(): any {
  }

  /**
   * Create empty attribute form.
   */
  public fillAttributeFormEmpty(): void {
    this.updateAttributeForm = this.formBuilder.group({
      attribute: null,
      customer: null
    });
  }

  /**
   * Filter club-overview attributes and open the edit attribute modal.
   */
  public editAttributes(): any {
    this.customerService.toggleAllModal(false);
    this.customerService.toggleEditAttributeDataModal(true);
  }

  /**
   * Updates the attribute on customer when clicking the toggle button
   */
  public submitCustomerAttributes(): any {
    this.spinnerService.show(this.spinnerSubmitName);
    const calls: Array<undefined | Observable<any>> = [];
    this.clubAttributes.forEach(clubAttr => {
      const customerAttribute = this.attributes.find(cusAttr => cusAttr.attribute['@id'] === clubAttr['@id']);
      this.updateAttributeForm.patchValue({
        attribute: clubAttr['@id'],
        customer: `/api/customers/${this.customer.id}`
      });
      if (clubAttr.checked) {
        if (customerAttribute === undefined) {
          calls.push(
            this.attributeService.createCustomerAttribute(this.updateAttributeForm.value)
          );
        }
      } else {
        if (customerAttribute) {
          calls.push(
            this.attributeService.deleteCustomerAttribute(customerAttribute.id).pipe(endWith([customerAttribute.id]))
          );
        }
      }
    });

    if (!calls.length) {
      this.customerService.toggleAllModal(false);
      this.spinnerService.hide(this.spinnerSubmitName);
    }

    forkJoin(calls).subscribe({
      next: attributes => {
        for (const attribute of attributes) {
          if (!Array.isArray(attribute)) {
            this.attributes.push(attribute);
          } else {
            const index = this.attributes.map(el => el.id).indexOf(attribute[0]);
            this.attributes.splice(index, 1);
          }
        }
        this.attributeService.announceCustomerAttributes(this.attributes);
        this.customerService.toggleAllModal(false);
        this.spinnerService.hide(this.spinnerSubmitName);
      },
      error: (error: Error) => {
        this.spinnerService.hide(this.spinnerSubmitName);
      }
    });
  }

  /**
   * Function that fetches all the attributes
   */
  public getAttributes(): void {
    this.attributeService.getClubAttribute(this.selectedClub.id).pipe(untilDestroyed(this))
      .subscribe((clubAttributes: any) => {
        this.attributeService.announceClubAttributes(clubAttributes);
        this.assignCheckedAttributes();
        this.spinnerService.hide(this.spinnerName);
      });
  }

  /**
   * Combines both array of attributes together to display the data
   */
  public assignCheckedAttributes(): void {
    for (const clubAttr of this.clubAttributes) {
      clubAttr.checked = this.attributes.some(cusAttr => cusAttr.attribute['@id'] === clubAttr['@id']);
    }
  }
}
